import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_ITEM_ADVANCED_SETTINGS,
  ItemAdvancedSettingsSchema,
  type CustomFieldsSchema,
  type ItemAdvancedSettings,
  type ItemSettings,
} from "@forgepoint/shared";
import isEqual from "fast-deep-equal";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useItemSettingsStore } from "@/store/itemSettingsStore";
import { useOrganizationStore } from "@/store/organizationStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { UnsavedChangesBar } from "@/modules/standardAgent/components/UnsavedChangesBar";
import { CustomFieldsSchemaEditor } from "./CustomFieldsSchemaEditor";
import { ItemDefaultValuesSettings } from "./ItemDefaultValuesSettings";
import { PriceBreakGroupFieldRemovalDialog } from "./PriceBreakGroupFieldRemovalDialog";
import { SectionLabel } from "./SectionLabel";

interface ItemSettingsPanelProps {
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
}

/**
 * Settings panel for the Item Settings tab: custom fields schema, item
 * default values and advanced settings, with a single sticky unsaved-changes bar for the page.
 * @param props - ItemSettingsPanelProps
 * @returns Item settings panel element
 */
export function ItemSettingsPanel({ isAdmin }: ItemSettingsPanelProps) {
  const settings = useItemSettingsStore((state) => state.settings);
  const lockedBy = useItemSettingsStore((state) => state.lockedBy);
  const isLoading = useItemSettingsStore((state) => state.isLoading);
  const isSaving = useItemSettingsStore((state) => state.isSaving);
  const hasFetched = useItemSettingsStore((state) => state.hasFetched);
  const fetchSettings = useItemSettingsStore((state) => state.fetchSettings);
  const updateSettings = useItemSettingsStore((state) => state.updateSettings);
  const [savedSchema, setSavedSchema] = useState<CustomFieldsSchema | null>(
    null,
  );
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);
  const savedEnableAllowance = settings?.enableAllowance ?? false;
  const [enableAllowance, setEnableAllowance] = useState(savedEnableAllowance);
  const [savedAdvancedSettings, setSavedAdvancedSettings] =
    useState<ItemAdvancedSettings>(DEFAULT_ITEM_ADVANCED_SETTINGS);
  const [advancedSettings, setAdvancedSettings] =
    useState<ItemAdvancedSettings>(DEFAULT_ITEM_ADVANCED_SETTINGS);
  const [pendingRemovedGroupField, setPendingRemovedGroupField] = useState<
    string | null
  >(null);

  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const isLoadingQuoteSettings = useQuoteSettingsStore(
    (state) => state.isLoading,
  );
  const quoteSettingsError = useQuoteSettingsStore((state) => state.error);
  const fetchQuoteSettings = useQuoteSettingsStore(
    (state) => state.fetchSettings,
  );
  const priceBreakGroupField = quoteSettings?.aggregatePriceListBreaks
    ? quoteSettings.priceListBreakGroupField
    : null;
  const removedGroupField = findRemovedField(
    priceBreakGroupField,
    savedSchema,
    schema,
  );

  const organization = useOrganizationStore((state) => state.organization);
  const updateOrganization = useOrganizationStore(
    (state) => state.updateOrganization,
  );
  const savedBomSequenceNumbers =
    organization?.bomSequenceNumbersEnabled === true;
  const [bomSequenceNumbers, setBomSequenceNumbers] = useState(
    savedBomSequenceNumbers,
  );
  const isOrganizationDirty = bomSequenceNumbers !== savedBomSequenceNumbers;
  const [isSavingOrganization, setIsSavingOrganization] = useState(false);

  const isDirty = useMemo(
    () =>
      JSON.stringify(schema) !== JSON.stringify(savedSchema) ||
      enableAllowance !== savedEnableAllowance ||
      !isEqual(advancedSettings, savedAdvancedSettings) ||
      isOrganizationDirty,
    [
      advancedSettings,
      enableAllowance,
      isOrganizationDirty,
      savedAdvancedSettings,
      savedEnableAllowance,
      savedSchema,
      schema,
    ],
  );

  useEffect(() => {
    if (hasFetched) {
      return;
    }
    fetchSettings().then(() => {
      const error = useItemSettingsStore.getState().error;
      if (error) {
        toast.error(error);
      }
    });
  }, [fetchSettings, hasFetched]);

  useEffect(() => {
    if (quoteSettings) {
      return;
    }
    fetchQuoteSettings();
  }, [fetchQuoteSettings, quoteSettings]);

  useEffect(() => {
    if (!hasFetched) {
      return;
    }
    const loaded = settings?.customFieldsSchema ?? null;
    setSavedSchema(loaded);
    setSchema(loaded);
    const loadedAdvancedSettings = toAdvancedSettings(settings);
    setSavedAdvancedSettings(loadedAdvancedSettings);
    setAdvancedSettings(loadedAdvancedSettings);
  }, [hasFetched, settings]);

  useEffect(() => {
    setEnableAllowance(savedEnableAllowance);
  }, [savedEnableAllowance]);

  useEffect(() => {
    setBomSequenceNumbers(savedBomSequenceNumbers);
  }, [savedBomSequenceNumbers]);

  const save = useCallback(async () => {
    if (isOrganizationDirty) {
      setIsSavingOrganization(true);
      const success = await updateOrganization({
        bomSequenceNumbersEnabled: bomSequenceNumbers,
      });
      setIsSavingOrganization(false);
      if (!success) {
        toast.error("Failed to update Bill of Materials settings.");
        return;
      }
    }

    const updatedSettings = await updateSettings({
      customFieldsSchema: schema,
      enableAllowance,
      ...advancedSettings,
    });
    if (!updatedSettings) {
      toast.error(
        useItemSettingsStore.getState().error ??
          "Failed to save item settings.",
      );
      return;
    }
    const saved = updatedSettings.customFieldsSchema;
    setSavedSchema(saved);
    setSchema(saved);
    const savedAdvanced = toAdvancedSettings(updatedSettings);
    setSavedAdvancedSettings(savedAdvanced);
    setAdvancedSettings(savedAdvanced);
    toast.success("Item settings saved.");
  }, [
    bomSequenceNumbers,
    advancedSettings,
    enableAllowance,
    isOrganizationDirty,
    schema,
    updateOrganization,
    updateSettings,
  ]);

  const requestSave = useCallback(async () => {
    if (!quoteSettings && isLoadingQuoteSettings) {
      toast.error("Quote settings are still loading. Try again in a moment.");
      return;
    }
    if (!quoteSettings && quoteSettingsError) {
      toast.error("Couldn't load quote settings. Refresh and try again.");
      return;
    }
    if (removedGroupField) {
      setPendingRemovedGroupField(removedGroupField);
      return;
    }
    await save();
  }, [
    isLoadingQuoteSettings,
    quoteSettings,
    quoteSettingsError,
    removedGroupField,
    save,
  ]);

  const confirmSave = useCallback(async () => {
    setPendingRemovedGroupField(null);
    await save();
  }, [save]);

  const discard = useCallback(() => {
    setSchema(savedSchema);
    setEnableAllowance(savedEnableAllowance);
    setAdvancedSettings(savedAdvancedSettings);
    setBomSequenceNumbers(savedBomSequenceNumbers);
  }, [
    savedBomSequenceNumbers,
    savedAdvancedSettings,
    savedEnableAllowance,
    savedSchema,
  ]);

  return (
    <div className="space-y-6 pt-4">
      {/* ── Custom Fields ── */}
      <SectionLabel title="Custom Fields" id="item-defaults-custom-fields" />
      {!hasFetched || isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <Card className="shadow-sm overflow-hidden">
          <div className="px-4 py-3.5">
            <p className="text-sm font-medium mb-1">Item custom fields</p>
            <p className="text-xs text-muted-foreground mb-3">
              Define organization-wide fields stored on items and synchronized
              with ERP part metadata.
            </p>
            <CustomFieldsSchemaEditor
              schema={schema}
              onChange={setSchema}
              disabled={!isAdmin || isSaving}
            />
          </div>
        </Card>
      )}

      {/* ── Default Values ── */}
      <ItemDefaultValuesSettings
        isAdmin={isAdmin}
        bomSequenceNumbersEnabled={bomSequenceNumbers}
        onBomSequenceNumbersChange={setBomSequenceNumbers}
        enableAllowance={enableAllowance}
        onEnableAllowanceChange={setEnableAllowance}
        advancedSettings={advancedSettings}
        onAdvancedSettingsChange={setAdvancedSettings}
        advancedSettingsLockedBy={lockedBy}
        isSaving={isSaving}
      />

      <UnsavedChangesBar
        isDirty={isAdmin && isDirty}
        onSave={requestSave}
        onDiscard={discard}
        isSaving={isSaving || isSavingOrganization}
      />

      <PriceBreakGroupFieldRemovalDialog
        fieldName={pendingRemovedGroupField}
        onCancel={() => setPendingRemovedGroupField(null)}
        onConfirm={confirmSave}
      />
    </div>
  );
}

/**
 * Picks the advanced settings out of the stored item settings.
 * @param settings - The item settings, or null when none are stored
 * @returns The advanced settings, or the defaults when none are stored
 */
function toAdvancedSettings(
  settings: ItemSettings | null,
): ItemAdvancedSettings {
  if (!settings) {
    return DEFAULT_ITEM_ADVANCED_SETTINGS;
  }
  return ItemAdvancedSettingsSchema.parse(settings);
}

/**
 * Finds the price break group field when an edit removes, renames, or makes it multi-value.
 * @param fieldName - The item custom field quotes combine price list breaks by, or null
 * @param savedSchema - The saved item custom fields schema
 * @param schema - The edited item custom fields schema
 * @returns The field name when the saved schema has it and the edited one lacks it or makes it an array, else null
 */
function findRemovedField(
  fieldName: string | null,
  savedSchema: CustomFieldsSchema | null,
  schema: CustomFieldsSchema | null,
): string | null {
  if (!fieldName || !savedSchema?.properties[fieldName]) {
    return null;
  }
  const editedField = schema?.properties[fieldName];
  if (editedField && editedField.type[0] !== "array") {
    return null;
  }
  return fieldName;
}
